import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';

/**
 * SD-1151: bridges the global header's "Trigger Workflow" modal to the Workflow
 * History view's run side panel. When a run is started from the header, it asks
 * the History view to pop open the panel for that run.
 *
 * The request is BOTH stored (so a History view that mounts *after* navigation can
 * pick it up on init) AND pushed live (so an already-mounted History view reacts
 * immediately). The History view consumes the pending request exactly once.
 */
@Injectable({ providedIn: 'root' })
export class WorkflowRunPanelService {
  private pendingRun: any = null;
  private readonly _openRun = new Subject<any>();
  /** Fires when a run should be shown in the History side panel. */
  readonly openRun$ = this._openRun.asObservable();

  /** Ask the History view to open its side panel for `run` (needs at least `runId` + `name`). */
  requestOpen(run: any): void {
    this.pendingRun = run;
    this._openRun.next(run);
  }

  /** Read and clear any pending open request (called by the History view on init). */
  takePending(): any {
    const run = this.pendingRun;
    this.pendingRun = null;
    return run;
  }

  // ── Live run updates ────────────────────────────────────────────────────────
  // The executor pushes a WorkflowRunUpdated event as each step starts and finishes.
  // NotificationsHubService receives it and republishes here, because the hub service is a
  // root singleton started at app boot while the History view mounts and unmounts — going
  // through this service keeps the hub free of any knowledge of who is currently listening.
  //
  // Deliberately NOT a replay subject: a late subscriber wants the run's current state, which
  // it fetches, not a backlog of transitions it already has.
  private readonly _runUpdated = new Subject<{ runId: string; status?: string; kind?: string }>();
  /** Fires when a run has moved server-side. The runId is what matters — the run panel re-fetches
   *  on it, so a dropped or out-of-order event can never leave it showing a status the server
   *  disagrees with.
   *
   *  `status` and `kind` ride along for listeners that only need the headline and would otherwise
   *  re-query for it: the History table has a whole page of runs and wants to repaint one pill,
   *  not reload the page on every step transition of every run on it. `kind` is 'run' or 'step',
   *  so a listener can tell the RUN reaching a status from a step within it doing so. */
  readonly runUpdated$ = this._runUpdated.asObservable();

  /** Called by NotificationsHubService on a WorkflowRunUpdated event. */
  notifyRunUpdated(runId: string, status?: string, kind?: string): void {
    if (runId) { this._runUpdated.next({ runId, status, kind }); }
  }
}
